import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Meta, Display, Chip } from '../../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../../components/states';
import { useApi, formatDate } from '../../lib/api';
import CareShell, { CareSection } from './CareShell';

/**
 * CareFertilization - lista nawożeń (z care_history filtrowane do action_type=fertilization).
 *
 * Endpoint:
 *   GET /api/care/user/all - cała historia user'a (spray + fertilization)
 */

const FERT_TYPE_LABEL = {
  mineral: 'Mineralny',
  organic: 'Organiczny',
  natural: 'Naturalny',
  biological: 'Biologiczny',
  other: 'Inny',
};

const FERT_TYPE_TONE = {
  mineral: 'amber',
  organic: 'sage',
  natural: 'sage',
  biological: 'sage',
  other: 'dim',
};

const METHOD_LABEL = {
  soil: 'Doglebowe',
  foliar: 'Dolistne',
  fertigation: 'Fertygacja',
  other: 'Inne',
};

function daysBetween(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

function FertRow({ entry, today }) {
  const tone = FERT_TYPE_TONE[entry.fertilizer_type] || 'dim';
  const typeLabel = FERT_TYPE_LABEL[entry.fertilizer_type] || entry.fertilizer_type || 'Nieokreślony';
  const isUpcoming = entry.next_application_date && entry.next_application_date >= today;
  const daysToNext = isUpcoming ? daysBetween(today, entry.next_application_date) : null;

  return (
    <div style={{
      padding: '16px 18px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 10,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 6,
      }}>
        <Display size={16} style={{ display: 'block', letterSpacing: '-.01em' }}>
          {entry.action_name || 'Nawożenie'}
        </Display>
        <Chip tone={tone}>{typeLabel}</Chip>
        {entry.is_recurring && <Chip tone="sage">↻ Cykliczne {entry.repeat_frequency || ''}</Chip>}
        {isUpcoming && (
          <Chip tone={daysToNext <= 7 ? 'amber' : 'dim'}>
            Następna za {daysToNext}d
          </Chip>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginBottom: 12 }}>
        {entry.plot_name} · Rząd {entry.row_number} · {entry.plant_name}
        {entry.plant_variety && ` (${entry.plant_variety})`}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10,
        fontSize: 12, color: 'var(--t-text-dim)',
        paddingTop: 10,
        borderTop: '1px solid var(--t-line)',
      }}>
        <div>
          <Meta style={{ marginBottom: 2 }}>Data</Meta>
          <span style={{ color: 'var(--t-text)' }}>{formatDate(entry.action_date)}</span>
        </div>
        {entry.npk_ratio && (
          <div>
            <Meta style={{ marginBottom: 2 }}>NPK</Meta>
            <span style={{ color: 'var(--t-text)', fontFamily: 'var(--t-mono)' }}>
              {entry.npk_ratio}
            </span>
          </div>
        )}
        {entry.application_method && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Metoda</Meta>
            <span style={{ color: 'var(--t-text)' }}>
              {METHOD_LABEL[entry.application_method] || entry.application_method}
            </span>
          </div>
        )}
        {entry.dosage && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Dawka</Meta>
            <span style={{ color: 'var(--t-text)' }}>{entry.dosage}</span>
          </div>
        )}
        {entry.next_application_date && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Następne</Meta>
            <span style={{ color: isUpcoming ? 'var(--t-amber)' : 'var(--t-text-dim)' }}>
              {formatDate(entry.next_application_date)}
            </span>
          </div>
        )}
      </div>
      {entry.note && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          fontSize: 13, color: 'var(--t-text-dim)',
          background: 'var(--t-bg)', borderRadius: 8,
          fontStyle: 'italic', lineHeight: 1.45,
        }}>{entry.note}</div>
      )}
    </div>
  );
}

export default function CareFertilization() {
  const [view, setView] = useState('all'); // 'all' | 'upcoming' | 'recurring'
  const { data, loading, error } = useApi('/api/care/user/all');

  if (error?.status === 401) {
    return <CareShell><ErrorState error={error} /></CareShell>;
  }
  if (loading) return <CareShell><LoadingState /></CareShell>;

  const today = new Date().toISOString().split('T')[0];
  const allFert = (data || []).filter(e => e.action_type === 'fertilization');
  const upcoming = allFert.filter(e => e.next_application_date && e.next_application_date >= today);
  const recurring = allFert.filter(e => e.is_recurring);

  let items = allFert;
  if (view === 'upcoming') items = upcoming;
  else if (view === 'recurring') items = recurring;

  return (
    <CareShell
      eyebrow="Nawożenie"
      title="Harmonogram odżywiania"
      subtitle="Zapis wszystkich nawożeń: typu nawozu, dawki, NPK, metody. Cykliczne pokażą Ci kiedy następna runda."
    >
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        marginBottom: 24,
      }}>
        <div style={{
          display: 'flex', gap: 4, padding: 4,
          background: 'var(--t-bg-elev)', borderRadius: 10,
          border: '1px solid var(--t-line)',
        }}>
          {[
            { k: 'all',       label: `Wszystkie (${allFert.length})` },
            { k: 'upcoming',  label: `Nadchodzące (${upcoming.length})` },
            { k: 'recurring', label: `Cykliczne (${recurring.length})` },
          ].map(opt => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setView(opt.k)}
              style={{
                padding: '8px 14px', borderRadius: 8,
                background: view === opt.k ? 'var(--t-bg)' : 'transparent',
                border: 'none', cursor: 'pointer',
                color: view === opt.k ? 'var(--t-text)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-sans)', fontSize: 13,
                fontWeight: view === opt.k ? 500 : 400,
              }}
            >{opt.label}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <Link to="/care/add?type=fertilization" style={{ textDecoration: 'none' }}>
          <Button variant="primary">+ Nowe nawożenie</Button>
        </Link>
      </div>

      {view === 'all' && upcoming.length > 0 && (
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          background: 'rgba(220, 180, 100, 0.08)',
          borderLeft: '3px solid var(--t-amber)',
          marginBottom: 24, fontSize: 13, color: 'var(--t-text)',
        }}>
          📅 <strong>{upcoming.length}</strong> {upcoming.length === 1 ? 'nawożenie czeka' : 'nawożeń czeka'} w najbliższym czasie.
        </div>
      )}

      <CareSection title={
        view === 'upcoming' ? 'Nadchodzące zabiegi' :
        view === 'recurring' ? 'Cykliczne zabiegi' : 'Wszystkie nawożenia'
      }>
        {items.length > 0 ? (
          <div>
            {items.map(entry => (
              <FertRow key={entry.id} entry={entry} today={today} />
            ))}
          </div>
        ) : (
          <EmptyState
            title={view === 'upcoming' ? 'Brak nadchodzących' :
                   view === 'recurring' ? 'Brak cyklicznych' : 'Brak nawożeń'}
            description={view === 'all'
              ? 'Dodaj pierwsze nawożenie, żeby śledzić harmonogram odżywiania.'
              : 'Spróbuj innego widoku lub dodaj nowy zabieg.'}
            action={
              <Link to="/care/add?type=fertilization" style={{ textDecoration: 'none' }}>
                <Button variant="primary">+ Dodaj nawożenie</Button>
              </Link>
            }
          />
        )}
      </CareSection>
    </CareShell>
  );
}
